import { supabase } from './supabase';

export interface SessaoAtiva {
  id: string;
  usuario_id: string;
  usuario_nome: string;
  usuario_login: string;
  filial_atual: string | null;
  ip_address: string | null;
  user_agent: string | null;
  data_login: string;
  ultima_atividade: string;
  status: 'ativa' | 'inativa';
}

class SessoesService {
  // Registrar sessão ao fazer login
  async registrarSessao(usuario: any, filialAtual?: string) {
    try {
      const dadosSessao = {
        usuario_id: usuario.id,
        usuario_nome: usuario.nome,
        usuario_login: usuario.login,
        filial_atual: filialAtual || null,
        ip_address: await this.obterIP(),
        user_agent: navigator.userAgent,
        status: 'ativa'
      };

      // Remover sessões anteriores do mesmo usuário
      await this.removerSessoesUsuario(usuario.id);

      // Inserir nova sessão
      const { error } = await supabase
        .from('sessoes_ativas')
        .insert(dadosSessao);

      if (error) {
        console.error('Erro ao registrar sessão:', error);
      }

      return { success: !error };
    } catch (error) {
      console.error('Erro ao registrar sessão:', error);
      return { success: false };
    }
  }

  // Remover sessão ao fazer logout
  async removerSessao(usuarioId: string) {
    try {
      const { error } = await supabase
        .from('sessoes_ativas')
        .delete()
        .eq('usuario_id', usuarioId)
        .eq('status', 'ativa');

      if (error) {
        console.error('Erro ao remover sessão:', error);
      }

      return { success: !error };
    } catch (error) {
      console.error('Erro ao remover sessão:', error);
      return { success: false };
    }
  }

  // Remover todas as sessões de um usuário
  async removerSessoesUsuario(usuarioId: string) {
    try {
      const { error } = await supabase
        .from('sessoes_ativas')
        .delete()
        .eq('usuario_id', usuarioId);

      if (error) {
        console.error('Erro ao remover sessões do usuário:', error);
      }

      return { success: !error };
    } catch (error) {
      console.error('Erro ao remover sessões do usuário:', error);
      return { success: false };
    }
  }

  // Atualizar última atividade
  async atualizarAtividade(usuarioId: string, filialAtual?: string) {
    try {
      const updateData: any = {
        ultima_atividade: new Date().toISOString()
      };

      if (filialAtual) {
        updateData.filial_atual = filialAtual;
      }

      const { error } = await supabase
        .from('sessoes_ativas')
        .update(updateData)
        .eq('usuario_id', usuarioId)
        .eq('status', 'ativa');

      if (error) {
        console.error('Erro ao atualizar atividade:', error);
      }

      return { success: !error };
    } catch (error) {
      console.error('Erro ao atualizar atividade:', error);
      return { success: false };
    }
  }

  // Buscar usuários online
  async buscarUsuariosOnline(): Promise<SessaoAtiva[]> {
    try {
      const { data, error } = await supabase
        .from('sessoes_ativas')
        .select('*')
        .eq('status', 'ativa')
        .gte('ultima_atividade', new Date(Date.now() - 30 * 60 * 1000).toISOString()) // últimos 30 minutos
        .order('ultima_atividade', { ascending: false });

      if (error) {
        console.error('Erro ao buscar usuários online:', error);
        return [];
      }

      return data || [];
    } catch (error) {
      console.error('Erro ao buscar usuários online:', error);
      return [];
    }
  }

  // Limpar sessões inativas
  async limparSessoesInativas() {
    try {
      const { error } = await supabase
        .rpc('limpar_sessoes_inativas');

      if (error) {
        console.error('Erro ao limpar sessões inativas:', error);
      }

      return { success: !error };
    } catch (error) {
      console.error('Erro ao limpar sessões inativas:', error);
      return { success: false };
    }
  }

  // Obter IP do cliente
  private async obterIP(): Promise<string> {
    try {
      // Tentar obter IP via API pública
      const response = await fetch('https://api.ipify.org?format=json');
      const data = await response.json();
      return data.ip || '0.0.0.0';
    } catch (error) {
      return '0.0.0.0';
    }
  }

  // Verificar se usuário está online
  async isUsuarioOnline(usuarioId: string): Promise<boolean> {
    try {
      const { data, error } = await supabase
        .from('sessoes_ativas')
        .select('id')
        .eq('usuario_id', usuarioId)
        .eq('status', 'ativa')
        .gte('ultima_atividade', new Date(Date.now() - 30 * 60 * 1000).toISOString())
        .single();

      return !error && !!data;
    } catch (error) {
      return false;
    }
  }
}

export default new SessoesService();
